import { useCallback, useEffect, useRef, useState } from "react";

import messages from "../messages/ja.json";

// Keep the success toast readable before the browser leaves the SPA.
export const REDIRECT_DELAY_MS = 1000;
// If this page is still alive this long after the navigation was requested, the hand-off
// never happened (unreachable host, blocked navigation, malformed URL that the browser
// silently ignored) — give control back to the user instead of leaving them on a frozen
// screen. Generous on purpose: a slow Salesforce response still unloads this document,
// which cancels the watchdog via `pagehide`.
const REDIRECT_TIMEOUT_MS = 8000;

function isNavigableUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
}

export interface ExternalRedirect {
  // True from the moment the hand-off starts until it either completes (this document is
  // unloaded) or is declared failed. Screens use it to block every further interaction.
  redirecting: boolean;
  start: (url: string, delayMs?: number) => void;
}

// Hands the browser over to an external URL (Salesforce) and reports failure so the
// caller can re-enable its own UI. Success is not observable from JS — the document is
// simply unloaded — so "did not fail within the timeout" is the only completion signal.
export function useExternalRedirect(onFail: (message: string) => void): ExternalRedirect {
  const [redirecting, setRedirecting] = useState(false);
  const timersRef = useRef<number[]>([]);
  // Read from timer callbacks, so a re-render never leaves them calling a stale handler.
  const onFailRef = useRef(onFail);
  onFailRef.current = onFail;

  const clearTimers = useCallback(() => {
    for (const id of timersRef.current) window.clearTimeout(id);
    timersRef.current = [];
  }, []);

  const fail = useCallback(() => {
    clearTimers();
    setRedirecting(false);
    onFailRef.current(messages.checkin.redirectError);
  }, [clearTimers]);

  const start = useCallback(
    (url: string, delayMs: number = REDIRECT_DELAY_MS) => {
      if (!isNavigableUrl(url)) {
        fail();
        return;
      }
      setRedirecting(true);
      timersRef.current.push(
        window.setTimeout(() => {
          try {
            window.location.assign(url);
          } catch {
            fail();
          }
        }, delayMs)
      );
      timersRef.current.push(window.setTimeout(fail, delayMs + REDIRECT_TIMEOUT_MS));
    },
    [fail]
  );

  useEffect(() => {
    // The navigation actually started: drop the watchdog so a slow-loading target page
    // can't be reported as a failure.
    const onPageHide = () => clearTimers();
    // Back-navigated here from the target page (bfcache restore): this document is live
    // again, so unlock it rather than leaving a dead overlay on screen.
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) fail();
    };
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
      clearTimers();
    };
  }, [clearTimers, fail]);

  return { redirecting, start };
}
